/**
 * Data layer for the Sales & Marketing dashboard
 * (`/marketing/sales-marketing`).
 *
 * Shopify and Amazon orders already land on `sales_orders` via the
 * `shopify-sync` / `amazon-sync` Edge Functions. There is no daily rollup
 * table for them (the Amazon tab of the Marketing Dashboard aggregates
 * client-side too), so this hook pages through the window, buckets by UTC
 * day + channel, and explodes `metadata.line_items` for the SKU drill-down.
 *
 * Amazon SP-API sync currently stores order headers only — no line items —
 * so the SKU table is Shopify-complete and Amazon orders without SKUs are
 * called out separately rather than silently dropped.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json, SalesChannelKind, SalesOrderStatus } from '@/types/database';
import {
  addUtcDays,
  fillDailyGapsInRange,
  pctChange,
  safeRatio,
  sumBy,
  type AnalyticsWindow,
} from '@/features/marketing/range';
import {
  salesDashWindow,
  type SalesChannelFilter,
  type SalesDashRange,
} from './range';

export type EcommerceKind = 'shopify' | 'amazon';

export interface SalesDashLineItem {
  name: string;
  sku: string | null;
  quantity: number;
  unitPriceCents: number;
}

export interface SalesDashOrder {
  id: string;
  soNumber: string;
  externalId: string | null;
  channelId: string | null;
  channelKind: EcommerceKind;
  channelName: string;
  day: string;
  orderedAt: string;
  status: SalesOrderStatus;
  totalCents: number;
  currency: string;
  customerEmail: string | null;
  customerName: string | null;
  lineItems: SalesDashLineItem[];
}

export interface SalesDailyPoint {
  day: string;
  revenueCents: number;
  orders: number;
  units: number;
  shopifyRevenueCents: number;
  amazonRevenueCents: number;
  shopifyOrders: number;
  amazonOrders: number;
}

export interface SalesTotals {
  revenueCents: number;
  orders: number;
  units: number;
  aovCents: number;
  shopifyRevenueCents: number;
  amazonRevenueCents: number;
  shopifyOrders: number;
  amazonOrders: number;
  cancelledOrders: number;
}

export interface SalesSkuRow {
  key: string;
  sku: string;
  name: string;
  channelKind: EcommerceKind | 'mixed';
  units: number;
  orders: number;
  revenueCents: number;
  share: number;
}

export interface SalesChannelMeta {
  id: string;
  name: string;
  kind: EcommerceKind;
  isActive: boolean;
}

export interface ChannelSalesData {
  connected: boolean;
  awaitingFirstSync: boolean;
  channels: SalesChannelMeta[];
  daily: SalesDailyPoint[];
  totals: SalesTotals;
  previous: SalesTotals;
  skus: SalesSkuRow[];
  orders: SalesDashOrder[];
  ordersWithoutSkus: number;
}

const PAGE_SIZE = 1000;
const MAX_ORDERS = 20_000;

type RawChannel = {
  id: string;
  name: string;
  kind: SalesChannelKind;
  is_active: boolean;
};

type RawOrder = {
  id: string;
  so_number: string;
  external_id: string | null;
  sales_channel_id: string | null;
  customer_email: string | null;
  status: SalesOrderStatus;
  ordered_at: string;
  total_cents: number;
  currency: string;
  metadata: Json | null;
};

function isEcommerceKind(kind: SalesChannelKind): kind is EcommerceKind {
  return kind === 'shopify' || kind === 'amazon';
}

function isEcommerceChannel(
  channel: RawChannel,
): channel is RawChannel & { kind: EcommerceKind } {
  return isEcommerceKind(channel.kind);
}

function emptyTotals(): SalesTotals {
  return {
    revenueCents: 0,
    orders: 0,
    units: 0,
    aovCents: 0,
    shopifyRevenueCents: 0,
    amazonRevenueCents: 0,
    shopifyOrders: 0,
    amazonOrders: 0,
    cancelledOrders: 0,
  };
}

function emptyDaily(day: string): SalesDailyPoint {
  return {
    day,
    revenueCents: 0,
    orders: 0,
    units: 0,
    shopifyRevenueCents: 0,
    amazonRevenueCents: 0,
    shopifyOrders: 0,
    amazonOrders: 0,
  };
}

function parseLineItems(metadata: Json | null): SalesDashLineItem[] {
  const bag = (metadata as Record<string, unknown> | null) ?? {};
  if (!Array.isArray(bag.line_items)) return [];
  return (bag.line_items as Array<Record<string, unknown>>).map((li) => ({
    name: String(li.name ?? li.title ?? 'Item'),
    sku: typeof li.sku === 'string' && li.sku.trim() ? li.sku.trim() : null,
    quantity: Number(li.quantity ?? 0) || 0,
    unitPriceCents: Number(li.unit_price_cents ?? 0) || 0,
  }));
}

function parseCustomerName(metadata: Json | null): string | null {
  const bag = (metadata as Record<string, unknown> | null) ?? {};
  const customer = (bag.customer as Record<string, unknown> | null) ?? null;
  if (!customer) return null;
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return name || null;
}

async function fetchOrdersInRange(
  channelIds: string[],
  startIso: string,
  endIso: string,
): Promise<RawOrder[]> {
  if (channelIds.length === 0) return [];
  const endExclusive = addUtcDays(endIso, 1);
  const rows: RawOrder[] = [];
  let from = 0;
  while (from < MAX_ORDERS) {
    const { data, error } = await supabase
      .from('sales_orders')
      .select(
        'id, so_number, external_id, sales_channel_id, customer_email, status, ordered_at, total_cents, currency, metadata',
      )
      .in('sales_channel_id', channelIds)
      .gte('ordered_at', `${startIso}T00:00:00Z`)
      .lt('ordered_at', `${endExclusive}T00:00:00Z`)
      .order('ordered_at', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const batch = (data ?? []) as RawOrder[];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return rows;
}

function toDashOrder(
  raw: RawOrder,
  channelById: Map<string, SalesChannelMeta>,
): SalesDashOrder | null {
  const channel = raw.sales_channel_id ? channelById.get(raw.sales_channel_id) : undefined;
  if (!channel) return null;
  return {
    id: raw.id,
    soNumber: raw.so_number,
    externalId: raw.external_id,
    channelId: channel.id,
    channelKind: channel.kind,
    channelName: channel.name,
    day: raw.ordered_at.slice(0, 10),
    orderedAt: raw.ordered_at,
    status: raw.status,
    totalCents: raw.total_cents,
    currency: raw.currency,
    customerEmail: raw.customer_email,
    customerName: parseCustomerName(raw.metadata),
    lineItems: parseLineItems(raw.metadata),
  };
}

function rollUpTotals(orders: SalesDashOrder[]): SalesTotals {
  const cancelled = orders.filter((o) => o.status === 'cancelled');
  const billable = orders.filter((o) => o.status !== 'cancelled');
  const revenueCents = sumBy(billable, (o) => o.totalCents);
  const shopify = billable.filter((o) => o.channelKind === 'shopify');
  const amazon = billable.filter((o) => o.channelKind === 'amazon');
  const units = sumBy(billable, (o) =>
    o.lineItems.length > 0
      ? sumBy(o.lineItems, (li) => li.quantity)
      : 0,
  );
  return {
    revenueCents,
    orders: billable.length,
    units,
    aovCents: safeRatio(revenueCents, billable.length),
    shopifyRevenueCents: sumBy(shopify, (o) => o.totalCents),
    amazonRevenueCents: sumBy(amazon, (o) => o.totalCents),
    shopifyOrders: shopify.length,
    amazonOrders: amazon.length,
    cancelledOrders: cancelled.length,
  };
}

function buildDaily(orders: SalesDashOrder[], window: AnalyticsWindow): SalesDailyPoint[] {
  const billable = orders.filter((o) => o.status !== 'cancelled');
  const byDay = new Map<string, SalesDailyPoint>();
  for (const order of billable) {
    const point = byDay.get(order.day) ?? emptyDaily(order.day);
    const units =
      order.lineItems.length > 0
        ? sumBy(order.lineItems, (li) => li.quantity)
        : 0;
    point.revenueCents += order.totalCents;
    point.orders += 1;
    point.units += units;
    if (order.channelKind === 'shopify') {
      point.shopifyRevenueCents += order.totalCents;
      point.shopifyOrders += 1;
    } else {
      point.amazonRevenueCents += order.totalCents;
      point.amazonOrders += 1;
    }
    byDay.set(order.day, point);
  }
  return fillDailyGapsInRange(
    Array.from(byDay.values()),
    window.currentStart,
    window.currentEnd,
    emptyDaily,
  );
}

function rankSkus(orders: SalesDashOrder[]): SalesSkuRow[] {
  const billable = orders.filter((o) => o.status !== 'cancelled');
  const byKey = new Map<
    string,
    {
      sku: string;
      name: string;
      kinds: Set<EcommerceKind>;
      units: number;
      orderIds: Set<string>;
      revenueCents: number;
    }
  >();

  for (const order of billable) {
    for (const li of order.lineItems) {
      const sku = li.sku || 'NO-SKU';
      const key = `${order.channelKind}:${sku}:${li.name}`;
      const existing = byKey.get(key) ?? {
        sku,
        name: li.name,
        kinds: new Set<EcommerceKind>(),
        units: 0,
        orderIds: new Set<string>(),
        revenueCents: 0,
      };
      existing.kinds.add(order.channelKind);
      existing.units += li.quantity;
      existing.orderIds.add(order.id);
      existing.revenueCents += li.quantity * li.unitPriceCents;
      if (!li.sku && li.name) existing.name = li.name;
      byKey.set(key, existing);
    }
  }

  const rows = Array.from(byKey.entries()).map(([key, row]) => ({
    key,
    sku: row.sku,
    name: row.name,
    channelKind: (row.kinds.size > 1 ? 'mixed' : [...row.kinds][0]) as EcommerceKind | 'mixed',
    units: row.units,
    orders: row.orderIds.size,
    revenueCents: row.revenueCents,
    share: 0,
  }));

  const totalRevenue = sumBy(rows, (r) => r.revenueCents);
  for (const row of rows) {
    row.share = safeRatio(row.revenueCents, totalRevenue);
  }
  rows.sort((a, b) => b.revenueCents - a.revenueCents || b.units - a.units);
  return rows;
}

export function useChannelSales(
  range: SalesDashRange,
  channelFilter: SalesChannelFilter,
) {
  const period = salesDashWindow(range);

  return useQuery({
    queryKey: [
      'sales-marketing',
      'orders',
      period.previousStart,
      period.previousEnd,
      period.currentStart,
      period.currentEnd,
      channelFilter,
    ],
    queryFn: async (): Promise<ChannelSalesData> => {
      const { data: channelData, error: channelErr } = await supabase
        .from('sales_channels')
        .select('id, name, kind, is_active')
        .in('kind', ['shopify', 'amazon']);
      if (channelErr) throw channelErr;

      const allChannels: SalesChannelMeta[] = ((channelData ?? []) as RawChannel[])
        .filter(isEcommerceChannel)
        .map((c) => ({
          id: c.id,
          name: c.name,
          kind: c.kind,
          isActive: c.is_active,
        }));

      const visibleChannels = allChannels.filter((c) => {
        if (!c.isActive) return false;
        if (channelFilter === 'all') return true;
        return c.kind === channelFilter;
      });
      const channelById = new Map(visibleChannels.map((c) => [c.id, c]));
      const channelIds = visibleChannels.map((c) => c.id);

      const [currentRaw, previousRaw] = await Promise.all([
        fetchOrdersInRange(channelIds, period.currentStart, period.currentEnd),
        fetchOrdersInRange(channelIds, period.previousStart, period.previousEnd),
      ]);

      const currentOrders = currentRaw
        .map((row) => toDashOrder(row, channelById))
        .filter((row): row is SalesDashOrder => row !== null);
      const previousOrders = previousRaw
        .map((row) => toDashOrder(row, channelById))
        .filter((row): row is SalesDashOrder => row !== null);

      const billable = currentOrders.filter((o) => o.status !== 'cancelled');
      const ordersWithoutSkus = billable.filter((o) => o.lineItems.length === 0).length;

      return {
        connected: currentOrders.length > 0 || previousOrders.length > 0,
        awaitingFirstSync: visibleChannels.length > 0 && currentOrders.length === 0 && previousOrders.length === 0,
        channels: visibleChannels,
        daily: buildDaily(currentOrders, period),
        totals: rollUpTotals(currentOrders),
        previous: rollUpTotals(previousOrders),
        skus: rankSkus(currentOrders),
        orders: currentOrders,
        ordersWithoutSkus,
      };
    },
  });
}

/**
 * Narrows a fetched sales payload to a single UTC day (chart click) and/or
 * a single SKU (table click). Filtering happens in memory so flipping
 * between days doesn't refetch.
 */
export function sliceSalesData(
  data: ChannelSalesData | undefined,
  selectedDay: string | null,
  selectedSkuKey: string | null,
): {
  totals: SalesTotals;
  previous: SalesTotals;
  skus: SalesSkuRow[];
  skuOrders: SalesDashOrder[];
  selectedSku: SalesSkuRow | null;
  deltaLabelOverride: string | null;
} {
  if (!data) {
    return {
      totals: emptyTotals(),
      previous: emptyTotals(),
      skus: [],
      skuOrders: [],
      selectedSku: null,
      deltaLabelOverride: null,
    };
  }

  let orders = data.orders;
  let deltaLabelOverride: string | null = null;
  let totals = data.totals;
  let previous = data.previous;

  if (selectedDay) {
    orders = orders.filter((o) => o.day === selectedDay);
    totals = rollUpTotals(orders);
    const priorDay = addUtcDays(selectedDay, -1);
    const priorPoint = data.daily.find((d) => d.day === priorDay);
    // The daily series only covers the current window, so the day before
    // a range's first day isn't on `daily`. Falling back to zeros rather
    // than refetching keeps the click snappy; the tile just omits the
    // delta when previous is empty.
    previous = priorPoint
      ? {
          ...emptyTotals(),
          revenueCents: priorPoint.revenueCents,
          orders: priorPoint.orders,
          units: priorPoint.units,
          aovCents: safeRatio(priorPoint.revenueCents, priorPoint.orders),
          shopifyRevenueCents: priorPoint.shopifyRevenueCents,
          amazonRevenueCents: priorPoint.amazonRevenueCents,
          shopifyOrders: priorPoint.shopifyOrders,
          amazonOrders: priorPoint.amazonOrders,
        }
      : emptyTotals();
    deltaLabelOverride = 'vs prior day';
  }

  const skus = rankSkus(orders);
  const selectedSku = selectedSkuKey
    ? skus.find((s) => s.key === selectedSkuKey) ?? data.skus.find((s) => s.key === selectedSkuKey) ?? null
    : null;

  let skuOrders: SalesDashOrder[] = [];
  if (selectedSku) {
    skuOrders = orders.filter((o) =>
      o.lineItems.some((li) => {
        const sku = li.sku || 'NO-SKU';
        return `${o.channelKind}:${sku}:${li.name}` === selectedSku.key;
      }),
    );
  }

  return { totals, previous, skus, skuOrders, selectedSku, deltaLabelOverride };
}

export function useSlicedSales(
  range: SalesDashRange,
  channelFilter: SalesChannelFilter,
  selectedDay: string | null,
  selectedSkuKey: string | null,
) {
  const query = useChannelSales(range, channelFilter);
  const sliced = useMemo(
    () => sliceSalesData(query.data, selectedDay, selectedSkuKey),
    [query.data, selectedDay, selectedSkuKey],
  );
  return { query, ...sliced };
}

export { pctChange };
